/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */
import _ from 'lodash';
import React from 'react';
import { decorate, computed, observable, action, runInAction } from 'mobx';
import { observer, inject } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Icon } from 'semantic-ui-react';

import { displayError } from '@amzn/base-ui/dist/helpers/notification';
import CopyToClipboard from '../../helpers/CopyToClipboard';

// expected props
// - scEnvironment (via prop)
// - scEnvironmentsStore (via injection)
class ScEnvironmentTextConnections extends React.Component {
    constructor(props) {
        super(props);
        runInAction(() => {
            this.revealedIds = {};
            this.secretValues = {};
            this.fetchingIds = {};
        });
    }

    get environment() {
        return this.props.scEnvironment;
    }

    get envsStore() {
        return this.props.scEnvironmentsStore;
    }

    getConnectionStore() {
        return this.envsStore.getScEnvConnectionStore(this.environment.id);
    }

    get connections() {
        const isText = (scheme) => scheme === 'text' || scheme === 'secret';
        return this.environment.getConnections((item) => isText(item.scheme));
    }

    handleRetrieveSecret = (connectionId) =>
        action(async () => {
            runInAction(() => {
                this.fetchingIds = { ...this.fetchingIds, [connectionId]: true };
            });
            try {
                const store = this.getConnectionStore();
                const result = await store.getSecretValue(connectionId);
                runInAction(() => {
                    this.secretValues = { ...this.secretValues, [connectionId]: result.value };
                    this.revealedIds = { ...this.revealedIds, [connectionId]: true };
                });
            } catch (error) {
                displayError(error);
            } finally {
                runInAction(() => {
                    this.fetchingIds = { ...this.fetchingIds, [connectionId]: false };
                });
            }
        });

    handleRevealToggle = (id) =>
        action(() => {
            this.revealedIds = { ...this.revealedIds, [id]: !this.revealedIds[id] };
        });

    render() {
        const connections = this.connections;
        if (_.isEmpty(connections)) return null;

        return (
            <div className="connection-card fadeIn animated" data-testid="text-connections">
                <div className="connection-card-header">
                    <div className="connection-card-title">
                        <Icon name="list alternate outline" />
                        Output Values
                    </div>
                </div>
                <div className="connection-card-body">
                    <div className="connection-intro">
                        These are output values from your workspace. Some values may be hidden for security
                        — click <b>Retrieve</b> to reveal them. Use the copy button to copy any value to your clipboard.
                    </div>
                    {_.map(connections, (item) => this.renderRow(item))}
                    <div className="connection-help-link">
                        <Icon name="question circle outline" style={{ margin: 0, lineHeight: 1, height: '1em' }} />
                        Need help? Visit the <a href="/documentation">Documentation</a> for detailed guides.
                    </div>
                </div>
            </div>
        );
    }

    renderRow(item) {
        if (item.scheme === 'secret') {
            return this.renderSecretRow(item);
        }
        return this.renderTextRow(item);
    }

    renderTextRow(item) {
        const value = item.info || item.url || '';
        return (
            <div className="credential-item" key={item.id}>
                <span className="credential-label">{item.name || 'Output'}</span>
                <span className="credential-value" data-testid={`text-connection-value-${item.id}`}>{value}</span>
                <div className="credential-actions">
                    <CopyToClipboard text={value} />
                </div>
            </div>
        );
    }

    renderSecretRow(item) {
        const fetched = this.secretValues[item.id];
        const isFetching = this.fetchingIds[item.id];
        const isRevealed = this.revealedIds[item.id];
        const hasFetched = fetched !== undefined;
        const displayValue = !hasFetched ? '••••••••••••' : isRevealed ? fetched : '••••••••••••';

        return (
            <div className="credential-item" key={item.id}>
                <span className="credential-label">{item.name || 'Output'}</span>
                {!hasFetched ? (
                    <div className="credential-actions" style={{ flex: 1 }}>
                        <button
                            className="connection-btn connection-btn-primary"
                            disabled={isFetching}
                            onClick={this.handleRetrieveSecret(item.id)}
                            data-testid={`secret-connection-retrieve-${item.id}`}
                        >
                            {isFetching
                                ? <Icon name="spinner" loading style={{ margin: 0, lineHeight: 1, height: '1em' }} />
                                : <Icon name="lock" style={{ margin: 0, lineHeight: 1, height: '1em' }} />}
                            Retrieve
                        </button>
                    </div>
                ) : (
                    <>
                        <span
                            className="credential-value"
                            data-testid={`text-connection-value-${item.id}`}
                            style={{ userSelect: !isRevealed ? 'none' : 'text' }}
                        >
                            {displayValue}
                        </span>
                        <div className="credential-actions">
                            <button
                                className="connection-btn connection-btn-secondary"
                                style={{ padding: '4px 10px' }}
                                onClick={this.handleRevealToggle(item.id)}
                                data-testid={`text-connection-reveal-${item.id}`}
                                aria-label={isRevealed ? 'Hide value' : 'Reveal value'}
                                title={isRevealed ? 'Hide value' : 'Reveal value'}
                            >
                                <Icon name={isRevealed ? 'eye slash' : 'eye'} style={{ margin: 0, lineHeight: 1, height: '1em' }} />
                            </button>
                            <CopyToClipboard text={fetched} />
                        </div>
                    </>
                )}
            </div>
        );
    }
}

decorate(ScEnvironmentTextConnections, {
    envsStore: computed,
    environment: computed,
    connections: computed,
    revealedIds: observable,
    secretValues: observable,
    fetchingIds: observable,
});

export default inject('scEnvironmentsStore')(withRouter(observer(ScEnvironmentTextConnections)));
